import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Building2, IdCard, KeyRound } from "lucide-react";
import { PageHead } from "@/components/AppLayout";
import { Card, Btn, Avatar, Spinner } from "@/components/ui-kit";
import { Modal, L } from "@/pages/Protocols";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", role: user?.role || "", hospital: user?.hospital || "",
    phone: user?.phone || "", license_no: user?.license_no || "",
  });
  const [pwOpen, setPwOpen] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => { await updateProfile(form); toast.success("Profile changes saved"); };

  return (
    <>
      <PageHead title="Profile" subtitle="Manage your clinician profile and preferences." />
      <Card className="max-w-xl p-6">
        <div className="flex items-center gap-4 mb-5">
          <Avatar name={form.name || "Dr"} size={60} />
          <div>
            <input className="pq-input font-display font-bold text-lg mb-1" value={form.name} onChange={set("name")} data-testid="profile-name-input" />
            <input className="pq-input text-sm" value={form.role} onChange={set("role")} data-testid="profile-role-input" />
          </div>
        </div>
        <Row icon={<Mail size={16} />} label="Email"><div className="text-sm font-semibold text-slate-700">{user?.email}</div></Row>
        {user?.prq_id && <Row icon={<IdCard size={16} />} label="PranIQ ID"><div className="text-sm font-semibold text-slate-700 font-mono" data-testid="profile-prq-id">{user.prq_id}</div></Row>}
        <Row icon={<Phone size={16} />} label="Phone"><input className="pq-input" value={form.phone} onChange={set("phone")} data-testid="profile-phone-input" /></Row>
        <Row icon={<Building2 size={16} />} label="Hospital"><input className="pq-input" value={form.hospital} onChange={set("hospital")} data-testid="profile-hospital-input" /></Row>
        <Row icon={<IdCard size={16} />} label="License No."><input className="pq-input" value={form.license_no} onChange={set("license_no")} data-testid="profile-license-input" /></Row>
        <div className="mt-5 flex gap-2 flex-wrap">
          <Btn variant="primary" data-testid="profile-save-button" onClick={save}>Save Changes</Btn>
          <Btn data-testid="change-password-button" onClick={() => setPwOpen(true)}><KeyRound size={15} /> Change Password</Btn>
        </div>
      </Card>
      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
    </>
  );
}

function ChangePasswordModal({ onClose }) {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.current_password || !f.new_password) return toast.error("Fill in all fields");
    if (f.new_password.length < 6) return toast.error("New password must be at least 6 characters");
    if (f.new_password !== f.confirm) return toast.error("New passwords do not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password changed successfully");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change password"); }
    setSaving(false);
  };
  return (
    <Modal title="Change Password" onClose={onClose}>
      <L label="Current Password"><input type="password" className="pq-input" data-testid="cp-current" value={f.current_password} onChange={set("current_password")} placeholder="Enter current password" /></L>
      <L label="New Password"><input type="password" className="pq-input" data-testid="cp-new" value={f.new_password} onChange={set("new_password")} placeholder="At least 6 characters" /></L>
      <L label="Confirm New Password"><input type="password" className="pq-input" data-testid="cp-confirm" value={f.confirm} onChange={set("confirm")} placeholder="Re-enter new password" /></L>
      <div className="flex justify-end gap-2 mt-4"><Btn onClick={onClose}>Cancel</Btn><Btn variant="primary" data-testid="cp-submit" onClick={submit} disabled={saving}>{saving ? <Spinner className="w-4 h-4" /> : "Update Password"}</Btn></div>
    </Modal>
  );
}

function Row({ icon, label, children }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-dashed border-slate-200 last:border-0">
      <div className="w-32 shrink-0 flex items-center gap-2 text-slate-500 text-[13px]">{icon}{label}</div>
      <div className="flex-1">{children}</div>
    </div>
  );
}
